"use client"

import { useState, useEffect, useRef, memo } from "react"
import type { HeroInventory } from "@/types/item.types"
import { ShopWindowItems } from "./shop-window-items"

interface ShopWindowProps {
  money: number
  inventory?: HeroInventory
  heroLevel: number
  onBuyItem: (itemDefId: string, price: number) => void
  onBuyCloneCharge: (cost: number) => void
  onSpendGold: (amount: number) => void
  viewMode?: "shop" | "quest"
  isMobile?: boolean
  // Quest board click handlers
  onMessagesClick?: () => void
  onLeaderboardClick?: () => void
  onDailyClimbClick?: () => void
  onDonateClick?: () => void
  // Toggle between shop and quest view (X button)
  onToggleView?: () => void
}

export const ShopWindow = memo(function ShopWindow({
  money,
  inventory,
  heroLevel,
  onBuyItem,
  onBuyCloneCharge,
  onSpendGold,
  viewMode = "shop",
  isMobile = false,
  onMessagesClick,
  onLeaderboardClick,
  onDailyClimbClick,
  onDonateClick,
  onToggleView,
}: ShopWindowProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [displayMode, setDisplayMode] = useState(viewMode)
  const [showItems, setShowItems] = useState(true)
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Initial mount animation
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true))
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  // Handle view mode transitions
  useEffect(() => {
    if (viewMode !== displayMode) {
      // Hide items and board
      setShowItems(false)
      setIsVisible(false)

      // After transition out, swap content and show again
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
      transitionTimeoutRef.current = setTimeout(() => {
        setDisplayMode(viewMode)
        setIsVisible(true)
        if (viewMode === "shop") {
          setShowItems(true)
        }
      }, 300)
    }
  }, [viewMode, displayMode])

  const bgImage = displayMode === "shop"
    ? "/images/shop-window-bg.png"
    : "/images/quest-window-bg.png"

  return (
    <div className="w-full flex justify-center absolute top-0 left-0 right-0" style={{ overflow: "visible" }}>
      <div className="relative" style={{ overflow: "visible" }}>
        <img
          src={bgImage}
          alt={displayMode === "shop" ? "Shop" : "Quest Board"}
          width={512}
          height={313}
          className={`w-[90vw] md:w-auto max-w-full h-auto transition-all duration-300 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"
          }`}
          style={{ imageRendering: "auto" }}
        />
        {/* X button touch target - appears on both shop and quest windows */}
        {onToggleView && (
          <button
            onClick={onToggleView}
            className="absolute pointer-events-auto hover:bg-white/20 transition-colors cursor-pointer rounded-full"
            style={{
              right: "1%",
              top: "8%",
              width: "8%",
              height: "14%",
            }}
            aria-label={displayMode === "shop" ? "Open quest board" : "Open shop"}
          />
        )}
        {displayMode === "shop" && inventory && (
          <ShopWindowItems
            money={money}
            inventory={inventory}
            heroLevel={heroLevel}
            onBuyItem={onBuyItem}
            onBuyCloneCharge={onBuyCloneCharge}
            onSpendGold={onSpendGold}
            isVisible={showItems && isVisible}
            isMobile={isMobile}
          />
        )}
        {/* Quest board clickable zones */}
        {displayMode === "quest" && (
          <div className="absolute inset-0 pointer-events-none">
            {/* MESSAGES - Left panel (+25px east, +30px south) */}
            <button
              onClick={onMessagesClick}
              className="absolute pointer-events-auto bg-red-500/30 hover:bg-red-500/50 transition-colors cursor-pointer"
              style={{
                left: "10.4%",
                top: "29.6%",
                width: "28%",
                height: "55%",
              }}
              aria-label="Messages"
            />

            {/* LEADERBOARD - Center panel (+10px east, +30px south) */}
            <button
              onClick={onLeaderboardClick}
              className="absolute pointer-events-auto bg-green-500/30 hover:bg-green-500/50 transition-colors cursor-pointer"
              style={{
                left: "38%",
                top: "29.6%",
                width: "28%",
                height: "55%",
              }}
              aria-label="Leaderboard"
            />

            {/* DAILY CLIMB - Right panel (+30px south, -40px height) */}
            <button
              onClick={onDailyClimbClick}
              className="absolute pointer-events-auto bg-blue-500/30 hover:bg-blue-500/50 transition-colors cursor-pointer"
              style={{
                left: "66.5%",
                top: "29.6%",
                width: "28%",
                height: "42.2%",
              }}
              aria-label="Daily Climb"
            />

            {/* DONATE - Bottom right (-20px north) */}
            <button
              onClick={onDonateClick}
              className="absolute pointer-events-auto bg-yellow-500/30 hover:bg-yellow-500/50 transition-colors cursor-pointer"
              style={{
                left: "66.5%",
                top: "71.6%",
                width: "28%",
                height: "16%",
              }}
              aria-label="Donate"
            />
          </div>
        )}
      </div>
    </div>
  )
})
